import React, { useEffect, useId } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, DialogContent, DialogTitle } from 'components/Dialog';
import { Box, Button, FormControl, FormHelperText } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Input from 'components/Input';
import { useContentLibrary } from 'features/content-library/useContentLibrary';
import { selectFolder } from 'features/content-library/contentLibrarySlice';
import { renameContentFolderRequested } from 'features/content-library/actions';
import { RenameContentLibraryItemPayload } from 'features/content-library/types';

export interface RenameContentLibraryFolderValues {
  folderName: string;
}

const RenameContentLibraryFolderModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const folderId = modalData.folderId as string;

  const folder = useAppSelector((state) => selectFolder(state, folderId));

  const { isFeatureAvailable } = useContentLibrary();

  const {
    handleSubmit,
    formState: { errors, isValid },
    control,
    setFocus,
  } = useForm<RenameContentLibraryFolderValues>({
    mode: 'onChange',
    defaultValues: {
      folderName: folder?.name || '',
    },
  });

  const shouldClose = !isFeatureAvailable || !folder;

  useEffect(() => {
    if (shouldClose) {
      dispatch(closeModal('renameContentLibraryFolder'));
    }
  }, [shouldClose, dispatch]);

  useEffect(() => {
    setTimeout(() => {
      setFocus('folderName');
    }, 0);
  }, [setFocus]);

  const handleCancel = () => {
    dispatch(closeModal('renameContentLibraryFolder'));
  };

  const handleOk: SubmitHandler<RenameContentLibraryFolderValues> = (data) => {
    if (folder) {
      const payload: RenameContentLibraryItemPayload = {
        id: folderId,
        name: data.folderName.trim(),
        personal: folder.personal,
      };

      dispatch(renameContentFolderRequested(payload));
      dispatch(closeModal('renameContentLibraryFolder'));
    }
  };

  return (
    <Dialog
      open={!shouldClose}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      onClose={handleCancel}
    >
      <DialogTitle id={`${modalId}-title`}>{t('content_library.rename_folder.title')}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 1,
          }}
        >
          <FormControl fullWidth error={!!errors.folderName}>
            <Controller
              name="folderName"
              control={control}
              rules={{
                required: t('common:validation.field_required'),
                validate: (value) => !!value.trim() || t('common:validation.field_required'),
              }}
              render={({ field: { ref, ...field } }) => (
                <Input
                  {...field}
                  autoFocus
                  inputProps={{
                    'aria-label': t('content_library.rename_folder.input_name_placeholder'),
                  }}
                  placeholder={t('content_library.rename_folder.input_name_placeholder')}
                  autoComplete="off"
                  inputRef={ref}
                />
              )}
            />
            <FormHelperText>{errors.folderName?.message}</FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="button" color="grayscale" onClick={handleCancel}>
          {t('content_library.rename_folder.btn_cancel_label')}
        </Button>
        <Button type="button" onClick={handleSubmit(handleOk)} disabled={!isValid}>
          {t('content_library.rename_folder.btn_ok_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameContentLibraryFolderModal;
